import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { X, Save, MapPin } from 'lucide-react';
import { createTileLayer } from '../utils/mapTiles';

export default function HubCardModal({ hub, isOpen, onClose, onSaveHub }) {
  if (!isOpen || !hub) return null;

  const [formData, setFormData] = useState({
    id: hub.id,
    name: hub.name || '',
    street: hub.address || hub.street || '',
    postal_code: hub.postal_code || '',
    city: hub.city || '',
    latitude: parseFloat(hub.latitude) || 46.52,
    longitude: parseFloat(hub.longitude) || 6.63
  });

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    const initialLat = parseFloat(formData.latitude) || 46.52;
    const initialLng = parseFloat(formData.longitude) || 6.63;

    if (!leafletMap.current) {
      leafletMap.current = L.map(mapRef.current, {
        center: [initialLat, initialLng],
        zoom: 14,
        zoomControl: true
      });

      createTileLayer('pale').addTo(leafletMap.current);
    }

    const map = leafletMap.current;

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }

    const customIcon = L.divIcon({
      className: 'draggable-hub-marker',
      html: `<div style="background:#0f172a; color:#fff; border:2px solid #38bdf8; border-radius:50%; width:34px; height:34px; display:flex; align-items:center; justify-content:center; font-size:18px; box-shadow:0 4px 10px rgba(0,0,0,0.5); cursor:grab;">🏭</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17]
    });

    const marker = L.marker([initialLat, initialLng], {
      icon: customIcon,
      draggable: true
    }).addTo(map);

    marker.on('dragend', (e) => {
      const latLng = e.target.getLatLng();
      const newLat = parseFloat(latLng.lat.toFixed(6));
      const newLng = parseFloat(latLng.lng.toFixed(6));

      setFormData(prev => ({
        ...prev,
        latitude: newLat,
        longitude: newLng
      }));
    });

    markerRef.current = marker;
    map.setView([initialLat, initialLng], map.getZoom());

  }, [formData.latitude, formData.longitude]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSaveHub(hub.id, formData);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="overlay-card" style={{ width: '760px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', borderRadius: '12px' }}>
        
        {/* Header */}
        <div style={{ background: '#1e293b', padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#f8fafc' }}>
              Éditer Hub #{formData.id} - {formData.name}
            </h2>
            <div style={{ fontSize: '0.78rem', color: '#38bdf8', marginTop: '2px' }}>
              📍 {formData.street}, {formData.postal_code} {formData.city}
            </div>
          </div>

          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Nom du Hub *</label>
            <input type="text" className="search-input" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Rue et Numéro *</label>
              <input type="text" className="search-input" required value={formData.street} onChange={e => setFormData({ ...formData, street: e.target.value })} />
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Code Postal (NPA) *</label>
              <input type="text" className="search-input" required value={formData.postal_code} onChange={e => setFormData({ ...formData, postal_code: e.target.value })} />
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Ville *</label>
              <input type="text" className="search-input" required value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#38bdf8', display: 'block', marginBottom: '4px' }}>Latitude (GPS)</label>
              <input type="number" step="0.000001" className="search-input" value={formData.latitude} onChange={e => setFormData({ ...formData, latitude: parseFloat(e.target.value) })} />
            </div>

            <div>
              <label style={{ fontSize: '0.78rem', color: '#38bdf8', display: 'block', marginBottom: '4px' }}>Longitude (GPS)</label>
              <input type="number" step="0.000001" className="search-input" value={formData.longitude} onChange={e => setFormData({ ...formData, longitude: parseFloat(e.target.value) })} />
            </div>
          </div>

          {/* Interactive Leaflet Map */}
          <div>
            <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
              🗺️ Positionnement Carte (Glissez le marqueur pour ajuster les coordonnées GPS du Hub)
            </label>
            <div ref={mapRef} style={{ width: '100%', height: '240px', borderRadius: '8px', border: '1px solid #334155', overflow: 'hidden' }} />
          </div>

          <div style={{ borderTop: '1px solid #334155', paddingTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="btn-action" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-action" style={{ background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', padding: '8px 18px' }}>
              <Save size={16} /> Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
